import type { PractitionerCardSource } from "./source";

type AddressSource = PractitionerCardSource["practiceAddresses"][number];

const STATES =
  "ACT|NSW|NT|QLD|SA|TAS|VIC|WA|Australian Capital Territory|New South Wales|Northern Territory|Queensland|South Australia|Tasmania|Victoria|Western Australia";
const WITH_STATE = new RegExp(`^(.*?)[\\s,]+(?:${STATES})(?:\\s+\\d{4})?$`, "i");
const STATE_ONLY = new RegExp(`^(?:${STATES})$`, "i");
const PLACE_NAME = /^[\p{L}][\p{L}' -]*$/u;

const asPlace = (value?: string, maxWords = Infinity): string | undefined => {
  const place = value?.trim();
  if (!place || STATE_ONLY.test(place) || !PLACE_NAME.test(place)) return undefined;
  return place.split(/\s+/).length <= maxWords ? place : undefined;
};

/* `formattedAddress` varies by source: a Google address ("460 Wildwood Rd, Wildwood VIC 3429,
   Australia"), free text from legacy records ("Melbourne, Victoria", "Niddrie", "Strathfield NSW"),
   or just the street line from the forms. For Google addresses `administrativeAreaLevel2` is the
   council ("Hume City"), so it is only used when the form left `formattedAddress` as a street line
   and put the suburb there. `locationName` is never used: on some records it is a business name. */
export const suburbOf = (address?: AddressSource): string | undefined => {
  if (!address) return undefined;

  const parts = (address.formattedAddress ?? "")
    .replace(/,\s*Australia$/i, "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const last = parts.at(-1);

  if (last) {
    const suburb = STATE_ONLY.test(last)
      ? asPlace(parts.at(-2))
      : asPlace(WITH_STATE.exec(last)?.[1] ?? last, parts.length > 1 ? Infinity : 2);
    if (suburb) return suburb;
  }

  return /\d/.test(parts[0] ?? "") ? asPlace(address.administrativeAreaLevel2) : undefined;
};
